import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HeartWordsImg } from 'src/assets/images';
import { useUserStore } from 'src/hooks/zustand/user';
import { Document, isBlockPage } from 'src/types/models';
import { Chip } from '@mui/material';
import HeartWordsDialog from '../../dialogs/HeartWordsSelectionDialog';
import * as actions from '../../actions';
import FeedbackCard, { CommonFeedbackCardProps } from '.';
import HeartCelebration from './HeartCelebration';

const HEART_WORDS_ACCENT = '#D14F73';

function HeartWordsCard(
  props: CommonFeedbackCardProps & {
    document: Document;
    isEditable?: boolean;
  },
) {
  const { document, isEditable = false } = props;
  const user = useUserStore((state) => state.user);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const heartWords = (document.meta.heartWords || []) as string[];
  const hasHeartWords =
    Array.isArray(heartWords) && heartWords.length;

  // Heart words can be set by teachers who own the document.
  const canSet =
    isEditable && user.id === document.user?.id && user.role === 'teacher';

  const heartWordsPresence = useMemo(() => {
    const presence: Record<string, boolean> = {};

    if (hasHeartWords && heartWords.length) {
      const allBlocks = document.version.pages
        .filter(isBlockPage)
        .flatMap((page) => page.grid.blocks);
      const text = allBlocks
        .map((b) => b.properties.plainText || '')
        .join(' ')
        .toLowerCase();

      heartWords.forEach((word) => {
        presence[word] = text.includes(word.toLowerCase());
      });
    }
    return presence;
  }, [document.version.pages, heartWords]);

  // Celebrate when a heart word transitions from not-present to present —
  // but only the FIRST time each word is used in this document. Once a word
  // has fired its celebration, it won't fire again (even if deleted + retyped).
  // Persisted in localStorage per document id so it survives reloads.
  const previousPresenceRef = useRef<Record<string, boolean>>({});
  const hasInitializedRef = useRef(false);
  const [bursts, setBursts] = useState<Array<{ id: string }>>([]);
  const shouldCelebrate = user.role === 'student';

  const celebratedKey = `pressto:heart-celebrated:${document.id}`;

  const readCelebrated = (): Set<string> => {
    try {
      const raw = window.localStorage.getItem(celebratedKey);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  };

  const markCelebrated = (newWords: string[]) => {
    try {
      const set = readCelebrated();
      newWords.forEach((w) => set.add(w));
      window.localStorage.setItem(celebratedKey, JSON.stringify(Array.from(set)));
    } catch {
      // Ignore quota / privacy errors — degrades to per-mount behavior.
    }
  };

  useEffect(() => {
    const prev = previousPresenceRef.current;

    // Skip the first run so we don't celebrate words that were already present
    // when the doc loaded.
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      previousPresenceRef.current = { ...heartWordsPresence };
      return;
    }

    if (shouldCelebrate) {
      const celebrated = readCelebrated();
      const newlyFound = Object.entries(heartWordsPresence).filter(
        ([word, isPresent]) => isPresent
          && !prev[word]
          && !celebrated.has(word.toLowerCase()),
      );
      if (newlyFound.length > 0) {
        markCelebrated(newlyFound.map(([word]) => word.toLowerCase()));
        const burstId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setBursts((b) => [...b, { id: burstId }]);
        // Remove the burst after the animation has had time to finish.
        setTimeout(() => {
          setBursts((b) => b.filter((x) => x.id !== burstId));
        }, 4500);
      }
    }

    previousPresenceRef.current = { ...heartWordsPresence };
  }, [heartWordsPresence, shouldCelebrate]);

  if (!canSet && !hasHeartWords) {
    return null;
  }

  const renderWordDetector = () => {
    if (hasHeartWords && heartWords) {
      return (
        <div className="heart-words-content">
          <div className="words-wrapper">
            {heartWords.map((word) => (
              <Chip
                key={word}
                label={word}
                className={heartWordsPresence[word] ? 'active' : ''}
              />
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <HeartCelebration bursts={bursts} />
    <FeedbackCard
      {...props}
      accentColor={HEART_WORDS_ACCENT}
      title="Heart Words"
      icon={HeartWordsImg}
    >
      {renderWordDetector()}

      {canSet && (
        <a
          href="#"
          onClick={() => {
            setIsDialogOpen(true);
          }}
          style={{
            color: '#163567',
            textDecoration: 'none',
            fontWeight: 'bold',
            fontSize: '.8rem',
            display: 'inline-block',
            margin: '8px 4px',
          }}
        >
          Edit
        </a>
      )}
      <HeartWordsDialog
        open={isDialogOpen}
        initialHeartWords={heartWords.join(', ')}
        onAdd={(words) => {
          let wordList: string[];
          if (words.trim().length) {
            wordList = words.split(/,\s*/);
          } else {
            wordList = [];
          }
          actions.saveDocument(document.id, {
            meta: {
              ...document.meta,
              heartWords: wordList,
            },
          });
          setIsDialogOpen(false);
        }}
        onClose={() => setIsDialogOpen(false)}
      />
    </FeedbackCard>
    </>
  );
}

export default HeartWordsCard;
